import { Coins, CreditCard, Tag, UserCheck } from "lucide-react";

/**
 * G1-S1 — canonical commercial-terms field group (live 8-set commercial half:
 * deferral · prepayment · creditLimit+currency · priceType). Shared by the
 * in-detail DraftTermsEditor AND the create-wizard "Комерційні умови" step so
 * the block is defined ONCE (D1=A reuse, no duplication). LE + dates are owned
 * by the surrounding surface (detail / wizard-meta). NO contract amount (AC-CR-11).
 */

export const inputCls =
  "h-9 w-full rounded-md border bg-background px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function FieldShell({
  label,
  required,
  badge,
  children,
}: {
  label: string;
  required?: boolean;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <span>
          {label}
          {required && <span className="ml-0.5 text-destructive">*</span>}
        </span>
        {badge}
      </label>
      {children}
    </div>
  );
}

export interface ContractTermsValue {
  deferralDays: string;
  prepayment: string;
  creditLimit: string;
  currency: string;
  priceTypeId: string;
}

export interface PriceTypeOption {
  id: string;
  name: string;
}

export function ContractTermsFields({
  value,
  onChange,
  priceTypes,
  priceTypesLoading,
}: {
  value: ContractTermsValue;
  onChange: (v: ContractTermsValue) => void;
  priceTypes: PriceTypeOption[];
  priceTypesLoading?: boolean;
}) {
  const set = (patch: Partial<ContractTermsValue>) => onChange({ ...value, ...patch });

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FieldShell label="Відстрочка, днів">
          <div className="flex items-center gap-1.5">
            <CreditCard className="h-3.5 w-3.5 shrink-0 text-primary" />
            <input
              type="number"
              min={0}
              inputMode="numeric"
              value={value.deferralDays}
              onChange={(e) => set({ deferralDays: e.target.value })}
              className={inputCls}
              placeholder="—"
            />
          </div>
        </FieldShell>
        <FieldShell label="Передоплата, %">
          <input
            type="number"
            min={0}
            max={100}
            step="0.1"
            value={value.prepayment}
            onChange={(e) => set({ prepayment: e.target.value })}
            className={inputCls}
            placeholder="—"
          />
        </FieldShell>
        <FieldShell
          label="Кредитний ліміт"
          badge={
            <span className="rounded-full bg-warning px-1.5 py-0.5 text-[10px] font-medium text-warning-foreground">
              потребує погодження Legal/Finance
            </span>
          }
        >
          <div className="flex items-center gap-1.5">
            <Coins className="h-3.5 w-3.5 shrink-0 text-primary" />
            <input
              type="number"
              min={0}
              step="1"
              value={value.creditLimit}
              onChange={(e) => set({ creditLimit: e.target.value })}
              className={inputCls}
              placeholder="—"
            />
            <select
              className="h-9 w-20 rounded-md border bg-background px-1.5 text-sm"
              value={value.currency}
              onChange={(e) => set({ currency: e.target.value })}
            >
              <option value="UAH">UAH</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
        </FieldShell>
        <FieldShell label="Тип цін" required>
          <div className="flex items-center gap-1.5">
            <Tag className="h-3.5 w-3.5 shrink-0 text-primary" />
            <select
              className={inputCls}
              value={value.priceTypeId}
              onChange={(e) => set({ priceTypeId: e.target.value })}
              disabled={priceTypesLoading}
            >
              <option value="">{priceTypesLoading ? "Завантаження…" : "Оберіть тип цін"}</option>
              {priceTypes.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </FieldShell>
      </div>
      <p className="text-[11px] text-muted-foreground">
        <UserCheck className="mr-1 inline h-3 w-3" />
        Тип цін — з BI-довідника (не вільний текст), обов'язковий перед поданням. Кредитний ліміт менеджер
        пропонує; чутливі умови підтверджує Legal/Finance при погодженні. Суму договору не зберігаємо.
      </p>
    </>
  );
}
